import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { Guide } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import { bulkAddGuideAssignees } from "@app/scene/onboarding/store/actions";

import AssignModal from "./AssignModal";
import styles from "./BulkActions.module.scss";

const messages = defineMessages({
  assignButton: {
    id: "onboarding.guides.bulk.actions.assign",
    defaultMessage: "Assign",
    description: "Assign button on the bulk actions panel on the Guides page",
  },
  assignModalSubTitle: {
    id: "onboarding.guides.bulk.assignModal.subTitle",
    defaultMessage: "Select teams and users below to assign the selected guides",
    description: "Subtitle of the Assign Modal for bulk actions on the Guides page",
  },
  title: {
    id: "onboarding.guides.bulk.title",
    defaultMessage: "{count} {count, plural, one {guide} other {guides}} selected",
    description: "Title on the bulk actions panel on the Guides page",
  },
});

export interface BulkActionsProps {
  guideIds: Guide["id"][];
  onResetSelection?: () => void;
}

interface Props extends BulkActionsProps {
  onBulkAddAssignees: typeof bulkAddGuideAssignees.request;
}

const BulkActions: FC<Props> = ({ guideIds, onBulkAddAssignees, onResetSelection }) => {
  const intl = useIntl();

  const [assignModalVisible, showAssignModal, hideAssignModal] = useBoolean();
  const handleChangeAssignees = useCallback(
    (userIds: User["id"][]) => {
      hideAssignModal();
      onBulkAddAssignees({ guideIds, userIds });
      onResetSelection?.();
    },
    [guideIds, hideAssignModal, onBulkAddAssignees, onResetSelection]
  );

  if (!guideIds.length) {
    return null;
  }

  return (
    <div className={styles.container}>
      {intl.formatMessage(messages.title, {
        count: guideIds.length,
      })}
      <Button onClick={showAssignModal} type="filled">
        {intl.formatMessage(messages.assignButton)}
      </Button>
      {assignModalVisible && (
        <AssignModal
          onChange={handleChangeAssignees}
          onHide={hideAssignModal}
          subTitle={intl.formatMessage(messages.assignModalSubTitle)}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = {
  onBulkAddAssignees: bulkAddGuideAssignees.request,
};

export default connect(null, mapDispatchToProps)(BulkActions);
